/**
 * Импорт редакции госкаталога СЗР с экрана — API-клиент + хуки (AK-781).
 *
 * Импорт идёт ФОНОМ: запуск отдаёт прогон (`run`), экран опрашивает его статус,
 * пока он `running`. Синхронного ответа быть не может — полный дамп (~19 МБ,
 * ~2600 препаратов) применяется минутами.
 *
 * Порядок работы оператора (SAFETY-протокол data-integrations):
 *   1) пробный прогон (`dry_run`) → цифры плана;
 *   2) человек смотрит цифры;
 *   3) `live` — применение.
 * Шаг 1 обязателен: экран не даёт применить, не показав план.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { pesticideSyncKeys } from '@/lib/pesticides/syncStatus';

export type ImportMode = 'dry_run' | 'live';
export type ImportSource = 'portal' | 'file';
export type ImportRunStatus = 'running' | 'success' | 'failed';

/** Counts прогона — та же форма, что печатает CLI-загрузчик. */
export interface ImportRunReport {
  dry_run: boolean;
  source_edition: string | null;
  products_total: number;
  products_written: number;
  substances_written: number;
  regulations_written: number;
  objects_written: number;
  objects_resolved: number;
  queued: number;
  errors: number;
  error_samples: string[];
  summary: string;
}

export interface ImportRun {
  id: string;
  status: ImportRunStatus;
  mode: ImportMode;
  source: ImportSource;
  source_name: string | null;
  source_edition: string | null;
  applied_edition_before: string | null;
  allow_older: boolean;
  progress_current: number;
  progress_total: number | null;
  report: ImportRunReport | null;
  error: string | null;
  /** Кто запустил — по нему экран отличает свой прогон от чужого. */
  started_by_name: string | null;
  started_at: string | null;
  finished_at: string | null;
}

/**
 * Машиночитаемые коды отказов бэка. Решения UI принимаются ПО НИМ, а не по
 * тексту сообщения: переформулировка текста не имеет права менять поведение
 * экрана (тот же анти-паттерн ловили в #967).
 */
export type ImportErrorCode =
  | 'edition_older'
  | 'edition_unknown'
  | 'edition_bad_format'
  | 'import_running'
  | 'file_missing'
  | 'file_empty'
  | 'file_too_large';

export interface StartImportInput {
  mode: ImportMode;
  source: ImportSource;
  file?: File | null;
  /** Ручной override даты редакции (страховка, когда паспорт её не отдал). */
  edition?: string | null;
  /** Осознанный откат к более старой редакции. */
  allowOlder?: boolean;
}

export async function startPesticideImport(
  input: StartImportInput,
): Promise<ImportRun> {
  const fd = new FormData();
  fd.append('mode', input.mode);
  fd.append('source', input.source);
  if (input.edition) fd.append('edition', input.edition);
  if (input.allowOlder) fd.append('allow_older', 'true');
  if (input.source === 'file' && input.file) fd.append('file', input.file);
  return api.uploadFile<ImportRun>(
    '/api/v1/reference/pesticides/import',
    fd,
  );
}

export async function fetchLatestImportRun(): Promise<ImportRun | null> {
  return api.get<ImportRun | null>(
    '/api/v1/reference/pesticides/import/runs/latest',
  );
}

export async function fetchImportRun(runId: string): Promise<ImportRun> {
  return api.get<ImportRun>(
    `/api/v1/reference/pesticides/import/runs/${runId}`,
  );
}

export const pesticideImportKeys = {
  latestRun: ['reference', 'pesticides', 'import', 'latest-run'] as const,
  run: (id: string) =>
    ['reference', 'pesticides', 'import', 'run', id] as const,
};

/**
 * Последний прогон. Пока он `running` — опрос раз в 2 секунды: оператор должен
 * видеть, что импорт живой, а не гадать, завис он или идёт.
 */
export function useLatestImportRun(
  opts: { enabled?: boolean } = {},
): UseQueryResult<ImportRun | null> {
  return useQuery({
    queryKey: pesticideImportKeys.latestRun,
    queryFn: fetchLatestImportRun,
    enabled: opts.enabled ?? true,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.status === 'running' ? 2000 : false,
  });
}

export function useStartPesticideImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: startPesticideImport,
    onSuccess: (run) => {
      qc.setQueryData(pesticideImportKeys.latestRun, run);
      void qc.invalidateQueries({ queryKey: pesticideImportKeys.latestRun });
      // Свежесть каталога меняется только после live-прогона, но перечитать
      // её дёшево и честнее, чем показывать старую редакцию рядом с отчётом
      // об импорте.
      void qc.invalidateQueries({ queryKey: pesticideSyncKeys.status(true) });
    },
  });
}
